<template>
  <div>
    <div class='field is-grouped'>
      <div class='control is-expanded'>
        <input
          class='input'
          type='text'
          ref='emailSearch'
          placeholder='Recipient Email, Domain, or Email ID'
          v-model='query'
          @keyup.enter='onSearch'
        />
      </div>

      <div class='control'>
        <span class='select'>
          <select @change='onClientSelected($event.target.value)' :value='clientId' :disabled='!clients.length'>
            <option :value='0' selected>All Clients</option>
            <option v-for='client in clients' :key='client.id' :value='client.id' :selected='clientId === client.id'>{{ client.name }}</option>
          </select>
        </span>
      </div>

      <div class='control'>
        <a :class='[{ "is-loading": isLoading }, "button"]' @click='onSearch'>
          Search
        </a>
      </div>
    </div>

    <hr>

    <ErrorNotification v-if='errors.length' :messages='errors' @clear='errors = []' />

    <EmailList v-if='list.length' :emails='list' :hideRcpt='isEmailQuery' @clientClicked='onClientSelected' />

    <div class="buttons is-centered">
      <a v-if='hasMoreResults' :class='[{ "is-loading": isLoadingMore }, "button"]' @click='onLoadMore'>
        Load More Results
      </a>
    </div>
  </div>

</template>

<script>
import { getClients, getEmails } from '@/request';
import EmailList from '@/components/EmailList.vue';
import ErrorNotification from '@/components/ErrorNotification.vue';

const PAGE_SIZE = 100;

export default {
  name: 'EmailSearch',

  components: {
    EmailList,
    ErrorNotification
  },

  data() {
    const { query, clientId, psmOffset, dmiOffset } = this.$route.query;
    return {
      query: query || '',
      queryType: null,
      isEmailQuery: false,
      clients: [],
      clientId: clientId || 0,
      list: [],
      errors: [],
      psmOffset: psmOffset || null,
      dmiOffset: dmiOffset || null,
      isLoading: false,
      isLoadingMore: false,
      isNavigating: false
    };
  },

  computed: {
    isDomainQuery() {
      return this.query[0] === '@' || this.query.includes('.');
    },

    hasMoreResults() {
      return !!(this.psmOffset || this.dmiOffset);
    },

    dynamicRoute() {
      let route = {
        path: '/',
        query: {}
      };

      if (this.query) {
        route.query.query = this.query;
      }
      if (this.clientId) {
        route.query.clientId = this.clientId;
      }

      return route;
    }
  },

  created() {
    this.pageSize = PAGE_SIZE;
  },

  async mounted() {
    
    const clients = await getClients();
    this.clients = clients;

    const { query, clientId } = this;

    if (clientId &&
      !clients.find(client => client.id === clientId)
    ) {
      this.clientId = 0;
    } else {
      this.clientId = clientId;
    }

    if (query) {
      this.query = query;
      await this.fetchEmails();
    } else {
      this.$nextTick(() => {
        this.$refs.emailSearch.focus();
      });
    }
  },
  
  watch: {
    '$route.query': {
      handler: function(newQuery, oldQuery) {
        // Handle route changes for query and client changes
        if (newQuery.query !== oldQuery.query || newQuery.clientId !== oldQuery.clientId) {
          this.query = newQuery.query || '';
          this.clientId = newQuery.clientId || 0;
          
          // Only fetch if this is external navigation (URL typed/pasted)
          // and not our own router.push() calls
          if (!this.isNavigating && this.query) {
            this.resetResults();
            this.fetchEmails();
          }
        }
      },
      deep: true
    }
  },

  methods: {
    async fetchEmails(isLoadMore = false) {
      if (isLoadMore) {
        this.isLoadingMore = true;
      } else {
        this.isLoading = true;
      }

      // Create params object with cursor tokens for pagination
      const params = {
        'page-size': PAGE_SIZE
      };

      // Add pagination cursor tokens if available (for load more)
      if (isLoadMore) {
        if (this.psmOffset) {
          params['psmOffset'] = this.psmOffset;
        }
        
        if (this.dmiOffset) {
          params['dmiOffset'] = this.dmiOffset;
        }
      }

      // Add client ID if present
      if (this.clientId) {
        params['api-client-id'] = this.clientId;
      }

      this.queryType = this.isDomainQuery ? 'rcpt' : 'id';
      
      console.debug('fetchEmails called:', {
        isLoadMore,
        psmOffset: this.psmOffset,
        dmiOffset: this.dmiOffset,
        query: this.query,
        queryType: this.queryType
      });
      
      try {
        const response = await getEmails(this.queryType, this.query, params);
        
        // Append results for load more, replace for initial search
        if (isLoadMore) {
          this.list = [...this.list, ...response.results];
        } else {
          this.list = response.results;
        }
        
        // Store the pagination tokens returned by each API for next page
        // These are opaque cursor tokens specific to each API
        this.psmOffset = response.psmOffset || null;
        this.dmiOffset = response.dmiOffset || null;
        
        console.debug('fetchEmails response:', {
          isLoadMore,
          newResultsCount: response.results.length,
          totalResultsCount: this.list.length,
          newPsmOffset: response.psmOffset,
          newDmiOffset: response.dmiOffset
        });
        
        if (!isLoadMore) {
          this.errors = [];
        }
      }
      catch (error) {
        console.error('fetchEmails error:', error);
        this.errors = [error.message];
        
        if (!isLoadMore) {
          this.resetResults();
        }
      } finally {
        if (isLoadMore) {
          this.isLoadingMore = false;
        } else {
          this.isLoading = false;
        }
      }
    },

    onSearch() {
      if (!this.query.trim().length) {
        this.errors = ['Please enter a Recipient Email address or Email ID.'];
        return;
      }

      this.setEmailQuery();
      this.resetResults();
      this.isNavigating = true;
      this.$router.push(this.dynamicRoute);
      this.fetchEmails();
      this.isNavigating = false;
    },

    onClientSelected(client) {
      if (client !== this.clientId) {
        this.clientId = client;
        if (this.query) {
          this.resetResults();
          this.isNavigating = true;
          this.$router.push(this.dynamicRoute);
          this.fetchEmails();
          this.isNavigating = false;
        } else {
          this.$router.push(this.dynamicRoute);
        }
      }
    },

    setEmailQuery() {
      // Quite the loose definition of email
      this.isEmailQuery = this.query.includes('@') && this.query[0] !== '@';
    },

    resetResults() {
      this.list = [];
      this.psmOffset = null;
      this.dmiOffset = null;
      this.errors = [];
    },

    onLoadMore() {
      // Load more results using current cursor tokens
      this.fetchEmails(true);
    }
  }
}
</script>
